import { IconProps } from '@tamagui/helpers-icon';
import { StarFull, ThumbsDown } from '@tamagui/lucide-icons';
import { Card, CardProps } from '@unistocks/components/layout/card';
import { Row } from '@unistocks/components/layout/flex';
import { SavingsGoalParagraph as Paragraph } from '@unistocks/features/savings-goals/components/cards/savings-goal-paragraph';
import { SavingsGoalProps } from '@unistocks/features/savings-goals/components/savings-goal-props';
import { formatCurrency, formatPercentage, t } from '@unistocks/shared/localization/index.ts';
import { memo } from 'react';
import { GetThemeValueForKey, Separator } from 'tamagui';

export const SavingsGoalHistory = memo<SavingsGoalProps & CardProps>(
  ({
    savingsGoal,
    goalCalculation: { annualReturns, totalReturns, netSavingsMonthly, netSavingsTotal },
    ...props
  }) => {
    // TODO: Convert based on contribution type if used
    const savingsGoalMonthlyContributions = savingsGoal.contribution;

    const annualReturnsDiff = annualReturns - savingsGoal.annualReturns;
    const annualReturnsDiffColor: GetThemeValueForKey<'color'> =
      annualReturns > savingsGoal.annualReturns
        ? '$positive'
        : annualReturns < savingsGoal.annualReturns
          ? '$negative'
          : '$bodyFont';
    const monthlyContributionsColor: GetThemeValueForKey<'color'> =
      netSavingsMonthly > savingsGoalMonthlyContributions
        ? '$positive'
        : netSavingsMonthly < savingsGoalMonthlyContributions
          ? '$negative'
          : '$bodyFont';

    const AnnualReturnsIcon =
      annualReturns > savingsGoal.annualReturns
        ? StarFull
        : annualReturnsDiff < 0
          ? ThumbsDown
          : null;
    const annualReturnsIconColor: IconProps['color'] =
      annualReturns > savingsGoal.annualReturns ? '$yellow10' : '$negative';
    const MonthlyContributionsIcon =
      netSavingsMonthly > savingsGoalMonthlyContributions
        ? StarFull
        : netSavingsMonthly < savingsGoalMonthlyContributions
          ? ThumbsDown
          : null;
    const monthlyContributionsIconColor: IconProps['color'] =
      netSavingsMonthly > savingsGoalMonthlyContributions ? '$yellow10' : '$negative';

    return (
      <Card {...props}>
        <Card.Header>
          <Card.Heading>{t('::SavingsGoal:History:Heading')}</Card.Heading>
        </Card.Header>
        <Card.Body>
          <Paragraph>
            {t('::SavingsGoal:AnnualReturns')}:{' '}
            <Paragraph fontWeight={'$7'}>
              {formatPercentage(savingsGoal.annualReturns)} {t('::PerYear')}
            </Paragraph>
          </Paragraph>
          <Row justifyContent="space-between">
            <Paragraph flex={1} whiteSpace="break-spaces">
              {t('::SavingsGoal:History:SinceStart')}:{'\n'}
              <Paragraph fontWeight={'$7'} color={annualReturnsDiffColor}>
                {formatPercentage(annualReturns)}
              </Paragraph>
              <Paragraph> {t('::CAGR').toLocaleLowerCase()}</Paragraph>
              {'\n'}
              <Paragraph fontWeight={'$7'} color={annualReturnsDiffColor}>
                {formatPercentage(totalReturns)}
              </Paragraph>
              <Paragraph> {t('::TotalReturns').toLocaleLowerCase()}</Paragraph>
            </Paragraph>
            {AnnualReturnsIcon ? (
              <AnnualReturnsIcon {...iconProps} color={annualReturnsIconColor} />
            ) : null}
          </Row>

          {savingsGoal.contribution ? (
            <>
              <Separator my={'$2'} />
              <Paragraph>
                {t('::SavingsGoal:History:ExpectedContributions')}:{' '}
                <Paragraph fontWeight={'$7'}>
                  {formatCurrency(savingsGoal.contribution, savingsGoal.currency.code, {
                    maximumFractionDigits: 0
                  })}{' '}
                  {t('::PerMonth')}
                </Paragraph>
              </Paragraph>
              <Row justifyContent="space-between">
                <Paragraph flex={1} whiteSpace="break-spaces">
                  {t('::SavingsGoal:History:SinceStart')}:{'\n'}
                  <Paragraph fontWeight={'$7'} color={monthlyContributionsColor}>
                    {formatCurrency(netSavingsMonthly, savingsGoal.currency.code, {
                      maximumFractionDigits: 0
                    })}
                  </Paragraph>
                  <Paragraph> {t('::PerMonth')}</Paragraph>
                  {'\n'}
                  <Paragraph fontWeight={'$7'} color={monthlyContributionsColor}>
                    {formatCurrency(netSavingsTotal, savingsGoal.currency.code, {
                      maximumFractionDigits: 0
                    })}
                  </Paragraph>
                  <Paragraph> {t('::Total').toLocaleLowerCase()}</Paragraph>
                </Paragraph>
                {MonthlyContributionsIcon ? (
                  <MonthlyContributionsIcon {...iconProps} color={monthlyContributionsIconColor} />
                ) : null}
              </Row>
            </>
          ) : null}
        </Card.Body>
      </Card>
    );
  }
);

SavingsGoalHistory.displayName = 'SavingsGoalHistory';

const iconProps: IconProps = {
  size: '$2.5',
  alignSelf: 'center'
};
